import { useEffect } from 'react';

const MAX_RECONNECT_ATTEMPTS = 15;

const getReconnectDelay = (attempt) => {
    if (attempt === 0) {
        return 500;
    }
    const delay = Math.min(2000, 500 * Math.pow(2, attempt));
    return delay;
};
export const useSessionReconnection = ({
    isConnected,
    isReconnecting,
    reconnectAttempts,
    hasAuthFailed,
    processingStatus,
    reconnectPendingOnShutdown,
    setIsReconnecting,
    setReconnectAttempts,
    setReconnectTrigger,
    setReconnectPendingOnShutdown,
    isMounted,
    isReconnectingRef,
    initializeSessionRef,
    reconnectTimerRef,
    sessionToReconnectRef,
    addMessage,
    removeMessage,
}) => {
    useEffect(() => {
        if (reconnectPendingOnShutdown && processingStatus !== 'processing') {
            console.log(`[useSessionReconnection] Processing finished. Executing pending reconnect loop.`);
            setReconnectPendingOnShutdown(false);
            if (!isReconnecting) {
                setIsReconnecting(true);
                setReconnectAttempts(0);
            }
        }
    }, [processingStatus, reconnectPendingOnShutdown, isReconnecting, addMessage, setReconnectPendingOnShutdown, setIsReconnecting, setReconnectAttempts]);
    useEffect(() => {
        if (!isReconnecting || isConnected) {
            if (isConnected && isReconnecting) {
                console.log("[useSessionReconnection] SSE is connected. Reconnection successful. Stopping loop.");
                if (isMounted.current) {
                    removeMessage('reconnect-notice');
                    setIsReconnecting(false);
                    setReconnectAttempts(0);
                    sessionToReconnectRef.current = null;
                }
            }
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            return;
        }
        if (hasAuthFailed) {
            console.error("[useSessionReconnection] Reconnection failed with auth error. Stopping loop.");
            if (isMounted.current) {
                setIsReconnecting(false);
                setReconnectAttempts(0);
                addMessage({ id: 'reconnect-notice', text: `Reconnection failed. Please log in again.`, severity: 'error' });
            }
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            return;
        }
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.error(`[useSessionReconnection] Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Stopping loop.`);
            if (isMounted.current) {
                setIsReconnecting(false);
                setReconnectAttempts(0);
                removeMessage('reconnect-notice');
                addMessage({
                    id: 'reconnect-error',
                    text: 'Unable to reconnect after multiple attempts.',
                    severity: 'error',
                    isLoading: false,
                    action: {
                        label: 'Try Again',
                        onClick: () => {
                            removeMessage('reconnect-error');
                            setIsReconnecting(true);
                            setReconnectAttempts(0);
                        }
                    }
                });
            }
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            return;
        }
        const delay = getReconnectDelay(reconnectAttempts);
        console.log(`[useSessionReconnection] Scheduling reconnect attempt #${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms.`);
        reconnectTimerRef.current = setTimeout(async () => {
            if (!isMounted.current || !isReconnectingRef.current) return;
            console.log(`[useSessionReconnection] Executing reconnect attempt #${reconnectAttempts + 1}`);
            const result = await initializeSessionRef.current(sessionToReconnectRef.current, {}, true);
            if (!isMounted.current || !isReconnectingRef.current) return;

            if (result) {
                // Backend session restored successfully.
                // Stop the loop and trigger SSE reconnection via reconnectTrigger.
                // If the subsequent SSE connection fails, useSseManager's onConnectionChange
                // handler will detect the failure and restart the reconnection loop from scratch.
                console.log(`[useSessionReconnection] Session ${result} restored. Stopping loop, triggering SSE reconnect.`);
                removeMessage('reconnect-notice');
                setIsReconnecting(false);
                setReconnectAttempts(0);
                sessionToReconnectRef.current = null;
                if (setReconnectTrigger) {
                    setReconnectTrigger(c => c + 1);
                }
            } else {
                // initializeSession returned null — a non-fatal failure.
                // Increment attempts and let the effect re-fire to schedule the next try.
                console.log(`[useSessionReconnection] Reconnect attempt #${reconnectAttempts + 1} failed. Will retry.`);
                setReconnectAttempts(prev => prev + 1);
            }
        }, delay);
        return () => {
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        };
    }, [
        isReconnecting,
        isConnected,
        reconnectAttempts,
        hasAuthFailed,
        addMessage,
        removeMessage,
        isMounted,
        isReconnectingRef,
        initializeSessionRef,
        reconnectTimerRef,
        sessionToReconnectRef,
        setIsReconnecting,
        setReconnectAttempts,
        setReconnectTrigger
    ]);
};